import React from 'react';
import { motion } from 'framer-motion';
import { useStakingContract } from '../../../hooks/useStakingContract';
import { useTokenData } from '../../../hooks/useTokenData';
import { CONFIG } from '../../../config';
import { Card } from '../../shared/Card';
import { formatEther } from 'viem';
import { 
  Gem, 
  Timer, 
  Users, 
  Clock,
  Wallet,
  Shield,
  Link,
  ExternalLink
} from 'lucide-react';

export function About() {
  const { data: stakingData, isLoading: isLoadingStaking } = useStakingContract();
  const { data: tokenData, isLoading: isLoadingToken } = useTokenData(CONFIG.GEM_TOKEN);

  if (isLoadingStaking || isLoadingToken) {
    return (
      <div className="w-full space-y-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 bg-blue-500/20 rounded" />
          <div className="h-4 w-2/3 bg-blue-500/20 rounded" />
        </div>
      </div>
    );
  }

  if (!stakingData || !tokenData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No contract data available</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-12 pb-16">
      {/* Contract Overview */}
      <motion.div 
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-white">
          $GEM Overview
        </h1>
        
        <Card>
          <Card.Content className="space-y-6">
            {/* Contract Addresses */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white">Contract Addresses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-500/10 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Shield className="w-4 h-4" />
                    <span>Staking Contract</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-white break-all">{CONFIG.STAKING_CONTRACT}</code>
                    <a
                      href={`https://apechain.calderaexplorer.xyz/address/${CONFIG.STAKING_CONTRACT}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 bg-blue-500/20 rounded-lg hover:bg-blue-500/30 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-blue-400" />
                    </a>
                  </div>
                </div>

                <div className="bg-blue-500/10 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Gem className="w-4 h-4" />
                    <span>GEM Token</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-white break-all">{CONFIG.GEM_TOKEN}</code>
                    <a
                      href={`https://apechain.calderaexplorer.xyz/address/${CONFIG.GEM_TOKEN}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 bg-blue-500/20 rounded-lg hover:bg-blue-500/30 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-blue-400" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contract Status */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white">Contract Status</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-500/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Shield className="w-4 h-4" />
                    <span>Status</span>
                  </div>
                  <div className="text-lg font-medium">
                    <span className={stakingData.paused ? "text-red-400" : "text-green-400"}>
                      {stakingData.paused ? "Paused" : "Active"}
                    </span>
                  </div>
                </div>

                <div className="bg-blue-500/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Users className="w-4 h-4" />
                    <span>Participants</span>
                  </div>
                  <div className="text-lg font-medium text-white">
                    {stakingData.participants.toString()}
                  </div>
                </div>

                <div className="bg-blue-500/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Wallet className="w-4 h-4" />
                    <span>Total Staked</span>
                  </div>
                  <div className="text-lg font-medium text-white">
                    {formatEther(stakingData.totalStakedTokens)} {tokenData.symbol}
                  </div>
                </div>

                <div className="bg-blue-500/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Gem className="w-4 h-4" />
                    <span>Reward Pool</span>
                  </div>
                  <div className="text-lg font-medium text-white">
                    {formatEther(stakingData.rewardPoolSize)} {tokenData.symbol}
                  </div>
                </div>
              </div>
            </div>

            {/* Token Information */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white">Token Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-blue-500/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Link className="w-4 h-4" />
                    <span>Token Name</span>
                  </div>
                  <div className="text-lg font-medium text-white">
                    {tokenData.name}
                  </div>
                </div>

                <div className="bg-blue-500/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Gem className="w-4 h-4" />
                    <span>Symbol</span>
                  </div>
                  <div className="text-lg font-medium text-white">
                    {tokenData.symbol}
                  </div>
                </div>

                <div className="bg-blue-500/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Timer className="w-4 h-4" />
                    <span>Total Supply</span>
                  </div>
                  <div className="text-lg font-medium text-white">
                    {formatEther(tokenData.totalSupply)} {tokenData.symbol}
                  </div>
                </div>
              </div>
            </div>
          </Card.Content>
        </Card>
      </motion.div>
    </div>
  );
}